import React, { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { Pagination, usePagination } from '../../../components/ui/Pagination';

interface TenantSubscription {
  id: string;
  packageId: string;
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  rolloverPolicy: 'ROLLOVER' | 'EXPIRE';
  metadata: any;
}

interface CreditAccount {
  standardSimulationCredits: number;
  premiumSimulationCredits: number;
  addOnCredits: number;
  lifetimeStandardAllocated: number;
  lifetimePremiumAllocated: number;
  lastAllocatedAt: string;
}

interface EffectiveEntitlement {
  canCreateStandardSimulation: boolean;
  canCreatePremiumSimulation: boolean;
  allowsOpenAITTS: boolean;
  allowsVideoAnalysis: boolean;
  allowedAIProviders: string[];
  maxSimulationsAllowed: number;
  isSubscriptionActive: boolean;
}

interface AddOnProduct {
  id: string;
  code: string;
  name: string;
  description: string;
  category: string;
  priceINR: number;
  standardCreditsGranted: number;
  premiumCreditsGranted: number;
  attemptsGranted: number;
  voicePassDays: number;
}

interface AddOnOrder {
  id: string;
  orderNumber: string;
  quantity: number;
  totalAmountINR: number;
  status: 'PENDING_APPROVAL' | 'COMPLETED' | 'REJECTED' | 'REFUNDED';
  metadata: any;
  createdAt: string;
}

export const TenantBillingPage: React.FC = () => {
  const [subscription, setSubscription] = useState<TenantSubscription | null>(null);
  const [pkg, setPkg] = useState<any>(null);
  const [account, setAccount] = useState<CreditAccount | null>(null);
  const [entitlements, setEntitlements] = useState<EffectiveEntitlement | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [products, setProducts] = useState<AddOnProduct[]>([]);
  const [orders, setOrders] = useState<AddOnOrder[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Purchase Modal State
  const [selectedProduct, setSelectedProduct] = useState<AddOnProduct | null>(null);
  const [orderQuantity, setOrderQuantity] = useState(1);
  const [orderNotes, setOrderNotes] = useState('');
  const [ordering, setOrdering] = useState(false);

  const {
    currentPage: ordPage,
    pageSize: ordPageSize,
    totalItems: ordTotalItems,
    paginatedItems: paginatedOrders,
    goToPage: setOrdPage,
    setPageSize: setOrdPageSize,
  } = usePagination(orders, 1, 20);

  const {
    currentPage: txPage,
    pageSize: txPageSize,
    totalItems: txTotalItems,
    paginatedItems: paginatedTransactions,
    goToPage: setTxPage,
    setPageSize: setTxPageSize,
  } = usePagination(transactions, 1, 20);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [subRes, prodRes, ordRes] = await Promise.all([
        api.get('/api/v1/billing/subscription'),
        api.get('/api/v1/billing/addons'),
        api.get('/api/v1/billing/addons/orders'),
      ]);

      const subData = subRes.data || {};
      setSubscription(subData.subscription || null);
      setPkg(subData.package || null);
      setAccount(subData.account || null);
      setEntitlements(subData.entitlements || null);
      setTransactions(subData.recentTransactions || []);

      setProducts(prodRes.data || []);
      setOrders(ordRes.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load billing information.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProduct) return;
    try {
      setOrdering(true);
      setError(null);
      await api.post('/api/v1/billing/addons/order', {
        productId: selectedProduct.id,
        quantity: orderQuantity,
        notes: orderNotes,
      });
      setSuccessMsg(`Order placed successfully! Awaiting Super Admin invoice verification.`);
      setSelectedProduct(null);
      setOrderQuantity(1);
      setOrderNotes('');
      loadBillingData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to place order.');
    } finally {
      setOrdering(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px', textAlign: 'center', color: 'var(--muted)' }}>
        Loading subscription and credit accounts...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto', padding: '24px 20px 80px' }}>
      <AdminPageHeader
        title="Subscription, Credits & Add-Ons"
        description="Manage your enterprise plan, monitor simulation creation balances, and request extra credits or voice passes."
      />

      {error && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--bad-soft)',
            color: 'var(--bad)',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(194,59,59,0.2)',
          }}
        >
          {error}
        </div>
      )}
      {successMsg && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--ok-soft)',
            color: 'var(--ok)',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(15,138,95,0.2)',
          }}
        >
          {successMsg}
        </div>
      )}

      {/* PLAN OVERVIEW BANNER */}
      <div
        style={{
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          padding: '28px',
          boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
          marginBottom: '28px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <h2 style={{ margin: 0, fontSize: '24px', color: 'var(--navy)', fontWeight: 700 }}>
              {pkg?.name || 'Starter Plan'}
            </h2>
            <span
              style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 700,
                background: subscription?.status === 'ACTIVE' ? 'var(--ok-soft)' : 'var(--bad-soft)',
                color: subscription?.status === 'ACTIVE' ? 'var(--ok)' : 'var(--bad)',
              }}
            >
              {subscription?.status || 'ACTIVE'}
            </span>
            <span
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 600,
                background: 'var(--surface)',
                color: 'var(--muted)',
                border: '1px solid var(--line)',
              }}
            >
              {pkg?.billingType || 'MONTHLY'} • ₹{pkg?.priceINR?.toLocaleString('en-IN') || '1,200'}
            </span>
          </div>

          <div style={{ fontSize: '13.5px', color: 'var(--muted)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <span>
              Period:{' '}
              <strong style={{ color: 'var(--ink)' }}>
                {subscription ? new Date(subscription.currentPeriodStart).toLocaleDateString() : 'Current'} —{' '}
                {subscription ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'Next Cycle'}
              </strong>
            </span>
            <span>•</span>
            <span>
              Rollover Policy:{' '}
              <strong style={{ color: 'var(--blue)' }}>{subscription?.rolloverPolicy || pkg?.rolloverPolicy || 'ROLLOVER'}</strong>
            </span>
            <span>•</span>
            <span>
              OpenAI Voice:{' '}
              <strong style={{ color: entitlements?.allowsOpenAITTS ? 'var(--ok)' : 'var(--muted)' }}>
                {entitlements?.allowsOpenAITTS ? 'Enabled' : 'Disabled (Standard Voices Only)'}
              </strong>
            </span>
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', color: 'var(--faint)', marginBottom: '4px' }}>Max Active Simulations</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--navy)' }}>
            {entitlements?.maxSimulationsAllowed || 50}
          </div>
        </div>
      </div>

      {/* REAL-TIME CREDIT BALANCE METERS */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '17px', color: 'var(--ink)', marginBottom: '16px', fontWeight: 700 }}>
          Available Simulation Creation Balances
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
          }}
        >
          {/* Standard Credits */}
          <div
            style={{
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)' }}>Standard Simulation Credits</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: 'var(--blue-soft)',
                  color: 'var(--blue)',
                }}
              >
                3 attempts / sim
              </span>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--navy)', marginBottom: '4px' }}>
              {account?.standardSimulationCredits ?? 0}
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--faint)' }}>
              1 credit consumed upon simulation creation. Grants 3 learner attempts.
            </p>
          </div>

          {/* Premium Credits */}
          <div
            style={{
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)' }}>Premium Simulation Credits</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: 'var(--ok-soft)',
                  color: 'var(--ok)',
                }}
              >
                10 attempts / sim
              </span>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--navy)', marginBottom: '4px' }}>
              {account?.premiumSimulationCredits ?? 0}
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--faint)' }}>
              Enables Premium OpenAI Voice. Grants 10 learner attempts per scenario.
            </p>
          </div>

          {/* Add-on Credits */}
          <div
            style={{
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)' }}>Add-on / Extra Credits</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: 'var(--surface)',
                  color: 'var(--muted)',
                }}
              >
                On-Demand
              </span>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--navy)', marginBottom: '4px' }}>
              {account?.addOnCredits ?? 0}
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--faint)' }}>
              Non-expiring add-on units for supplemental attempt expansion packs.
            </p>
          </div>
        </div>
      </div>

      {/* ADD-ON CATALOG STORE */}
      <div style={{ marginBottom: '36px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--ink)', fontWeight: 700 }}>Add-On Purchase Catalog</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
              Order extra simulation creation credits, attempt expansion packs, or a 30-day Premium OpenAI Voice pass.
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px',
          }}
        >
          {products.map((prod) => (
            <div
              key={prod.id}
              style={{
                background: 'var(--white)',
                border: '1px solid var(--line)',
                borderRadius: '12px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
              }}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--navy)' }}>{prod.name}</h4>
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--ink)' }}>
                    ₹{prod.priceINR?.toLocaleString('en-IN')}
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '0 0 14px', minHeight: '36px' }}>
                  {prod.description}
                </p>
                <div style={{ fontSize: '12px', color: 'var(--faint)', marginBottom: '16px' }}>
                  {prod.standardCreditsGranted > 0 && `• +${prod.standardCreditsGranted} Standard Credits (3 attempts ea)\n`}
                  {prod.premiumCreditsGranted > 0 && `• +${prod.premiumCreditsGranted} Premium Credits (10 attempts ea)\n`}
                  {prod.attemptsGranted > 0 && `• +${prod.attemptsGranted} Additional Attempts\n`}
                  {prod.voicePassDays > 0 && `• ${prod.voicePassDays}-Day OpenAI TTS Access\n`}
                </div>
              </div>

              <button
                onClick={() => setSelectedProduct(prod)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--blue)',
                  color: 'var(--white)',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Purchase Add-On
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* RECENT ORDERS TABLE */}
      <div style={{ marginBottom: '36px' }}>
        <h3 style={{ fontSize: '18px', color: 'var(--ink)', marginBottom: '12px', fontWeight: 700 }}>
          Your Add-On Orders &amp; Invoices
        </h3>
        {orders.length === 0 ? (
          <div
            style={{
              padding: '28px',
              textAlign: 'center',
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              color: 'var(--muted)',
            }}
          >
            No previous add-on orders.
          </div>
        ) : (
          <div
            style={{
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
            }}
          >
            <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
              <thead>
                <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Order Number</th>
                  <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Product</th>
                  <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Total (INR)</th>
                  <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Date</th>
                  <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((ord) => (
                  <tr key={ord.id} style={{ borderBottom: '1px solid var(--line)' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--ink)' }}>{ord.orderNumber}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontWeight: 600 }}>{ord.metadata?.productName || ord.metadata?.itemCode}</span>
                      <span style={{ color: 'var(--muted)', marginLeft: '6px' }}>x{ord.quantity}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 700 }}>₹{ord.totalAmountINR?.toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--muted)' }}>
                      {new Date(ord.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 700,
                          background:
                            ord.status === 'COMPLETED'
                              ? 'var(--ok-soft)'
                              : ord.status === 'PENDING_APPROVAL'
                              ? 'var(--warn-soft)'
                              : 'var(--bad-soft)',
                          color:
                            ord.status === 'COMPLETED'
                              ? 'var(--ok)'
                              : ord.status === 'PENDING_APPROVAL'
                              ? 'var(--warn)'
                              : 'var(--bad)',
                        }}
                      >
                        {ord.status === 'PENDING_APPROVAL' ? 'PENDING APPROVAL' : ord.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {ordTotalItems > 0 && (
              <Pagination
                currentPage={ordPage}
                totalItems={ordTotalItems}
                pageSize={ordPageSize}
                onPageChange={setOrdPage}
                onPageSizeChange={setOrdPageSize}
                showPageSizeSelector
                pageSizeOptions={[10, 20, 50, 100]}
                itemLabel="orders"
              />
            )}
          </div>
        )}
      </div>

      {/* RECENT CREDIT TRANSACTIONS LEDGER */}
      <div>
        <h3 style={{ fontSize: '18px', color: 'var(--ink)', marginBottom: '12px', fontWeight: 700 }}>
          Credit Ledger &amp; Usage History
        </h3>
        {transactions.length === 0 ? (
          <div
            style={{
              padding: '28px',
              textAlign: 'center',
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              color: 'var(--muted)',
            }}
          >
            No credit transactions recorded yet.
          </div>
        ) : (
          <div
            style={{
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
            }}
          >
            <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
                  <th style={{ padding: '10px 16px', color: 'var(--navy)' }}>Date</th>
                  <th style={{ padding: '10px 16px', color: 'var(--navy)' }}>Type</th>
                  <th style={{ padding: '10px 16px', color: 'var(--navy)' }}>Action</th>
                  <th style={{ padding: '10px 16px', color: 'var(--navy)' }}>Amount</th>
                  <th style={{ padding: '10px 16px', color: 'var(--navy)' }}>Balance After</th>
                  <th style={{ padding: '10px 16px', color: 'var(--navy)' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((tx) => (
                  <tr key={tx.id} style={{ borderBottom: '1px solid var(--line)' }}>
                    <td style={{ padding: '10px 16px', color: 'var(--muted)' }}>
                      {new Date(tx.createdAt).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 16px', fontWeight: 600 }}>{tx.creditType}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ fontWeight: 600, color: tx.amount > 0 ? 'var(--ok)' : 'var(--bad)' }}>
                        {tx.action}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', fontWeight: 700, color: tx.amount > 0 ? 'var(--ok)' : 'var(--bad)' }}>
                      {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                    </td>
                    <td style={{ padding: '10px 16px', fontWeight: 600 }}>{tx.balanceAfter}</td>
                    <td style={{ padding: '10px 16px', color: 'var(--muted)', fontSize: '12px' }}>
                      {tx.metadata?.notes || tx.referenceType}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {txTotalItems > 0 && (
              <Pagination
                currentPage={txPage}
                totalItems={txTotalItems}
                pageSize={txPageSize}
                onPageChange={setTxPage}
                onPageSizeChange={setTxPageSize}
                showPageSizeSelector
                pageSizeOptions={[10, 20, 50, 100]}
                itemLabel="transactions"
              />
            )}
          </div>
        )}
      </div>

      {/* PLACE ORDER MODAL */}
      {selectedProduct && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(7, 29, 69, 0.4)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: 'var(--white)',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '480px',
              padding: '28px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--navy)' }}>Order Add-On</h3>
              <button
                onClick={() => setSelectedProduct(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--muted)' }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: '16px', padding: '14px', background: 'var(--surface)', borderRadius: '8px' }}>
              <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{selectedProduct.name}</div>
              <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>{selectedProduct.description}</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--navy)', marginTop: '8px' }}>
                ₹{selectedProduct.priceINR?.toLocaleString('en-IN')} per unit
              </div>
            </div>

            <form onSubmit={handlePlaceOrder}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                  Quantity
                </label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={orderQuantity}
                  onChange={(e) => setOrderQuantity(Math.max(1, Number(e.target.value)))}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                  Billing / PO Notes (Optional)
                </label>
                <input
                  type="text"
                  placeholder="PO number or department reference"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                />
              </div>

              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: 'var(--muted)' }}>Total Amount:</span>
                <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--navy)' }}>
                  ₹{(selectedProduct.priceINR * orderQuantity).toLocaleString('en-IN')}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--white)',
                    color: 'var(--ink)',
                    border: '1px solid var(--line)',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={ordering}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--blue)',
                    color: 'var(--white)',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {ordering ? 'Submitting...' : 'Submit Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
